import type { ReactNode } from 'react';

interface DigitGridProps {
  // Currently-entered digits. Length zero through `length`; anything
  // past the requested length is ignored so a controlled caller cannot
  // overflow the visible cells.
  readonly value: string;
  readonly length: number;
  // When true, the renderer substitutes a bullet glyph for every typed
  // digit so a shoulder-surfer can't read the PIN off the screen.
  readonly masked?: boolean;
  // Adds the .error class to every cell. Callers usually toggle this
  // for one frame on a mismatch / invalid-code error and clear it on
  // the next keystroke.
  readonly error?: boolean;
}

// Six-or-four-cell box display used by /activate and /change-pin. The
// component is purely presentational — input arrives through the
// caller's keypad / hidden input, this just renders cells.
export const DigitGrid = (props: DigitGridProps): ReactNode => {
  const { value, length, masked = false, error = false } = props;
  const cells: ReactNode[] = [];
  for (let i = 0; i < length; i += 1) {
    const d = value[i];
    const filled = d !== undefined && d !== '';
    const isCurrent = !error && !filled && i === value.length;
    const cls = [
      'digit-cell',
      filled ? 'filled' : '',
      isCurrent ? 'current' : '',
      error ? 'error' : '',
    ]
      .filter(Boolean)
      .join(' ');
    cells.push(
      <div key={i} className={cls} aria-hidden="true">
        {filled ? (masked ? '•' : d) : ''}
      </div>,
    );
  }
  return (
    <div className={`digit-row digit-row-${String(length)}`}>{cells}</div>
  );
};
